<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose when the rule is active.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charging</span>
                    <p class="section-heading__copy">Set base charges, hourly rate and included time.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Base charges</h5>
                            <p class="charging-group__hint">Flag fall and minimum charge prior to hourly billing.</p>
                            <div class="field-pair">
                                <form-group name="hourlyFlagfall" label="Flag Fall">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.hourlyFlagfall)"
                                        v-model="rateDetail.hourlyFlagfall.operator"
                                        :value="rateDetail.hourlyFlagfall.operator"
                                    />
                                    <md-input v-model.number="rateDetail.hourlyFlagfall.value" />
                                </form-group>
                                <form-group name="hourlyMinimumCharge" label="Minimum Charge">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.hourlyMinimumCharge)"
                                        v-model="rateDetail.hourlyMinimumCharge.operator"
                                        :value="rateDetail.hourlyMinimumCharge.operator"
                                    />
                                    <md-input v-model.number="rateDetail.hourlyMinimumCharge.value" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Hourly rate</h5>
                            <p class="charging-group__hint">Primary charge applied per hour, and time Adjustment (minutes)</p>
                            <form-group name="hourlyRatePerHour" label="Hourly Rate">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.hourlyRatePerHour)"
                                    v-model="rateDetail.hourlyRatePerHour.operator"
                                    :value="rateDetail.hourlyRatePerHour.operator"
                                />
                                <md-input v-model.number="rateDetail.hourlyRatePerHour.value" />
                            </form-group>
                            <form-group name="hourlyTimeAdjustment" label="Time Adjustment">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.hourlyTimeAdjustment)"
                                    v-model="rateDetail.hourlyTimeAdjustment.operator"
                                    :value="rateDetail.hourlyTimeAdjustment.operator"
                                />
                                <md-input v-model.number="rateDetail.hourlyTimeAdjustment.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Inclusions</h5>
                            <p class="charging-group__hint">Set included time (minutes) before additional hourly charges apply.</p>
                            <div class="field-pair">
                                <form-group name="hourlyMinimumTime" label="Minimum Time">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.hourlyMinimumTime)"
                                        v-model="rateDetail.hourlyMinimumTime.operator"
                                        :value="rateDetail.hourlyMinimumTime.operator"
                                    />
                                    <md-input v-model.number="rateDetail.hourlyMinimumTime.value" />
                                </form-group>
                                <form-group name="includedTime" label="Included Time">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.includedTime)"
                                        v-model="rateDetail.includedTime.operator"
                                        :value="rateDetail.includedTime.operator"
                                    />
                                    <md-input v-model.number="rateDetail.includedTime.value" />
                                </form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Duration & rounding</span>
                    <p class="section-heading__copy">Select statuses that define duration and configure rounding rules.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="status-selector">
                        <h6 class="charging-group__title">Duration statuses</h6>
                        <p class="charging-group__hint">Select exactly two statuses to calculate duration.</p>
                        <multiselect
                            v-model="rateDetail.hourlyStatuses"
                            :options="statues"
                            :multiple="true"
                            :close-on-select="false"
                            :max="maxStatus"
                            placeholder="Select up to two statuses"
                        ></multiselect>
                    </div>

                    <div class="status-selector">
                        <h6 class="charging-group__title">Rounding mode</h6>
                        <p class="charging-group__hint">Choose how captured time is rounded when calculating charges.</p>
                        <multiselect
                            ref="roundingModeContainer"
                            v-model="selectedRoundingMode"
                            :options="roundingOptions"
                            :multiple="true"
                            :close-on-select="true"
                            placeholder="Rounding mode"
                            label="label"
                            track-by="label"
                            :reduce="(x) => x.key"
                            :max="1"
                        ></multiselect>
                    </div>

                    <form-group name="roudingTo" label="Rounding To (Minutes)">
                        <md-input v-if="isUpdate && updatingRule" v-model="rateDetail.roundTo" />
                        <md-input v-if="!isUpdate || !updatingRule" v-model.number="rateDetail.roundTo" />
                    </form-group>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import { RATE_CHARGING_TYPES, STATUS_SHIPMENT_CONSTANTS, RATES_HOURLY_ROUNDING_MODE } from '@/utils/constants';
import { decimal } from 'vuelidate/lib/validators';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateHourlyRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('hourlyRatePerHour');
        this.$_ensureChargeField('hourlyFlagfall');
        this.$_ensureChargeField('hourlyMinimumCharge');
        this.$_ensureChargeField('hourlyMinimumTime');
        this.$_ensureChargeField('includedTime');
        this.$_ensureChargeField('hourlyTimeAdjustment');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: 'test',
                customerId: null
            },
            rateChargingTypes: RATE_CHARGING_TYPES,
            updatingRule: false,
            statues: STATUS_SHIPMENT_CONSTANTS,
            maxStatus: 2,
            roundingOptions: RATES_HOURLY_ROUNDING_MODE,
            selectedRoundingMode: null
        };
    },
    watch: {
        selectedRoundingMode(newValue, oldValue) {
            if (this.$refs.roundingModeContainer) {
                if (newValue !== oldValue) {
                    this.rateDetail.roundingMode = newValue[0]?.key ?? null;
                }
            }
        },
        'rateDetail.hourlyMinimumTime.value': {
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.includes(':')) {
                    this.$set(
                        this.rateDetail.hourlyMinimumTime,
                        'value',
                        this.formatTimeSpan(newVal)
                    );
                }
            },
            immediate: true
        },
        'rateDetail.includedTime.value': {
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.includes(':')) {
                    this.$set(
                        this.rateDetail.includedTime,
                        'value',
                        this.formatTimeSpan(newVal)
                    );
                }
            },
            immediate: true
        },
        'rateDetail.hourlyTimeAdjustment.value': {
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.includes(':')) {
                    this.$set(
                        this.rateDetail.hourlyTimeAdjustment,
                        'value',
                        this.formatTimeSpan(newVal)
                    );
                }
            },
            immediate: true
        }
    },
    validations: {
        rateDetail: {
            hourlyRatePerHour: {
                value: {
                    decimal
                }
            },
            hourlyFlagfall: {
                value: {
                    decimal
                }
            },
            hourlyMinimumCharge: {
                value: {
                    decimal
                }
            },
            hourlyMinimumTime: {
                value: {
                    decimal
                }
            },
            includedTime: {
                value: {
                    decimal
                }
            },
            hourlyStatuses: {},
            hourlyTimeAdjustment: {
                value: {
                    decimal
                }
            },
            roundTo: {
                decimal
            },
            roundingMode: {
                decimal
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    mounted() {
        if (this.isUpdate) {
            this.updatingRule = true;
            // this.rateDetail.hourlyMinimumTime = this.formatTimeSpan(this.rateDetail.hourlyMinimumTime.value);
            // this.rateDetail.includedTime = this.formatTimeSpan(this.rateDetail.includedTime.value);
            this.rateDetail.roundTo = this.formatTimeSpan(this.rateDetail.roundTo);

            const mode = this.rateDetail.roundingMode;
            if (mode) {
                this.selectedRoundingMode = this.roundingOptions.find((x) => x.key === mode);
            }
        }
    },
    methods: {
        validateData() {
            if (
                this.$v.rateDetail.hourlyRatePerHour.value.$model === null &&
                this.$v.rateDetail.hourlyFlagfall.value.$model === null &&
                this.$v.rateDetail.hourlyMinimumCharge.value.$model === null &&
                this.$v.rateDetail.hourlyMinimumTime.value.$model === null &&
                this.$v.rateDetail.includedTime.value.$model === null &&
                this.$v.rateDetail.hourlyStatuses.$model === null &&
                this.$v.rateDetail.hourlyTimeAdjustment.value.$model === null &&
                this.$v.rateDetail.roundTo.$model === null &&
                this.$v.rateDetail.roundingMode.$model === null
            ) {
                return false;
            }

            if (
                this.rateDetail.hourlyStatuses &&
                this.rateDetail.hourlyStatuses.length !== 2 &&
                this.rateDetail.hourlyStatuses.length !== 0
            )
                return false;

            return true;
        },
        formatTimeSpan(time) {
            let timeCopy = time;
            if (timeCopy === null || timeCopy === '00:00:00') 
                return null;

            let [day] = timeCopy.split('.').map(Number);

            if (day >= 1) {
                timeCopy = timeCopy.substring(timeCopy.indexOf('.') + 1);
            } else {
                day = 0;
            }

            const [hours, minutes] = timeCopy.split(':').map(Number);
            const totalMinutes = day * 24 * 60 + hours * 60 + minutes;

            if (totalMinutes === 0) {
                return null;
            }

            return totalMinutes;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'PROCESSING...');

            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === this.$route.params.type);
            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            this.updatingRule = false;

            this.rateDetail.hourlyMinimumTime = this.rateDetail.hourlyMinimumTime;
            this.rateDetail.includedTime = this.rateDetail.includedTime;
            this.rateDetail.hourlyTimeAdjustment = this.rateDetail.hourlyTimeAdjustment;

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            if (this.selectedRoundingMode && !this.rateDetail.roundingMode) {
                this.rateDetail.roundingMode = this.selectedRoundingMode.key ?? null;
            }

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>